
<template>
  <nav :class="['sidebar', { collapsed }]">
    <ul>
      <!-- 1) All menu items except logout -->
      <li v-for="item in nonLogoutItems" :key="item.name">
        <component
          :is="item.to ? 'router-link' : 'button'"
          v-bind="item.to ? { to: item.to } : {}"
          @click="item.action && $emit(item.action)"
          :class="[ item.to ? 'menu-link' : 'menu-action' ]"
        >
          <i :class="['fas', `fa-${item.icon}`]" :title="item.name" aria-hidden="true"></i>
          <span>{{ item.name }}</span>
        </component>
      </li>

      <!-- 2) Marketplace link above logout -->
      <li key="Marketplace">
        <router-link to="/marketplace" class="menu-link">
          <i class="fas fa-shopping-bag" title="Marketplace" aria-hidden="true"></i>
          <span>Marketplace</span>
        </router-link>
      </li>

      <!-- 3) Logout/Signout actions -->
      <li v-for="item in logoutItems" :key="item.name">
        <button @click="$emit(item.action)" class="menu-action logout">
          <i :class="['fas', `fa-${item.icon}`]" :title="item.name" aria-hidden="true"></i>
          <span>{{ item.name }}</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'SidebarMenu',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    // Filter out logout items
    const nonLogoutItems = computed(() =>
      props.items.filter(item => item.action !== 'logout')
    )
    const logoutItems = computed(() =>
      props.items.filter(item => item.action === 'logout')
    )

    return {
      nonLogoutItems,
      logoutItems
    }
  }
}
</script>

<style scoped>
/* (existing scoped styles remain unchanged) */
.sidebar {
  background: var(--sidebar-bg);
  color: var(--sidebar-text);
  width: var(--sidebar-width);
  transition: width 0.3s ease;
  border-right: 1px solid var(--sidebar-border);
  overflow: hidden;
}

.sidebar.collapsed {
  width: var(--sidebar-collapsed-width);
}

.sidebar ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.sidebar li + li {
  border-top: 1px solid var(--sidebar-border);
}

.menu-link,
.menu-action {
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  color: var(--sidebar-text);
  background: none;
  border: none;
  text-decoration: none;
  font: inherit;
  border-radius: var(--space-xs);
  transition: background 0.2s, color 0.2s;
}

.menu-link i,
.menu-action i {
  margin-right: var(--space-sm);
  font-size: 1.2rem;
}

.sidebar.collapsed .menu-link span,
.sidebar.collapsed .menu-action span {
  display: none;
}

.menu-link:hover,
.menu-action:hover,
.menu-link:focus,
.menu-action:focus {
  background: var(--sidebar-hover-bg);
  outline: none;
}

.menu-link.router-link-active {
  background: var(--sidebar-active-bg);
  color: var(--sidebar-active-text);
}

.menu-action.logout {
  color: #DC2626;
  background: #FEF2F2;
}
.menu-action.logout:hover {
  background: #FEE2E2;
}

@media (max-width: 1024px) {
  .sidebar {
    width: var(--sidebar-collapsed-width);
  }
  .sidebar span {
    display: none;
  }
  .menu-link i,
  .menu-action i {
    margin: 0 auto;
    display: block;
    width: 100%;
    text-align: center;
  }
}
</style>


<style scoped>
/* 1. Base sidebar styling */
.sidebar {
  background: var(--sidebar-bg);
  color: var(--sidebar-text);
  width: var(--sidebar-width);
  transition: width 0.3s ease;
  border-right: 1px solid var(--sidebar-border);
  overflow: hidden;
}

/* 2. Collapsed (icon-only) width */
.sidebar.collapsed {
  width: var(--sidebar-collapsed-width);
}

/* 3. Reset list and separators */
.sidebar ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.sidebar li + li {
  border-top: 1px solid var(--sidebar-border);
}

/* 4. Link & button base styles */
.menu-link,
.menu-action {
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  color: var(--sidebar-text);
  background: none;
  border: none;
  text-decoration: none;
  font: inherit;
  border-radius: var(--space-xs);
  transition: background 0.2s, color 0.2s;
}

/* 5. Icon spacing */
.menu-link i,
.menu-action i {
  margin-right: var(--space-sm);
  font-size: 1.2rem;
}

/* 6. Hide labels when collapsed */
.sidebar.collapsed .menu-link span,
.sidebar.collapsed .menu-action span {
  display: none;
}

/* 7. Hover & focus states */
.menu-link:hover,
.menu-action:hover,
.menu-link:focus,
.menu-action:focus {
  background: var(--sidebar-hover-bg);
  outline: none;
}

/* 8. Active route highlighting */
.menu-link.router-link-active {
  background: var(--sidebar-active-bg);
  color: var(--sidebar-active-text);
}

/* 9. Danger style for logout */
.menu-action.logout {
  color: #DC2626;
  background: #FEF2F2;
}
.menu-action.logout:hover {
  background: #FEE2E2;
}

/* 10. Mobile & small screens: icon-only by default */
@media (max-width: 1024px) {
  .sidebar {
    width: var(--sidebar-collapsed-width);
  }
  /* Hide labels explicitly */
  .sidebar span {
    display: none;
  }
  /* Center icons in collapsed width */
  .menu-link i,
  .menu-action i {
    margin: 0 auto;
    display: block;
    width: 100%;
    text-align: center;
  }
}
</style>
